import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { apiaryService, hiveService, inspectionService } from '../services/firebase';
import { trackDataExport } from '../utils/analytics';

const ExportPage: React.FC = () => {
  const { user } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [exportType, setExportType] = useState<'csv' | 'json'>('csv');
  const [exportScope, setExportScope] = useState<'all' | 'inspections' | 'apiaries' | 'hives'>('all');
  const [message, setMessage] = useState('');

  const formatDate = (date: any) => {
    if (!date) return '';
    if (date.toDate && typeof date.toDate === 'function') {
      return date.toDate().toISOString().split('T')[0];
    }
    if (date instanceof Date) {
      return date.toISOString().split('T')[0];
    }
    if (typeof date === 'string') {
      return new Date(date).toISOString().split('T')[0];
    }
    return String(date);
  };

  const convertToCSV = (data: any[], headers: string[]) => {
    const csvRows = [];
    
    // Add headers
    csvRows.push(headers.join(','));
    
    // Add data rows
    for (const row of data) {
      const values = headers.map(header => {
        const value = row[header];
        if (value === null || value === undefined) return '';
        
        // Handle dates
        if (header.includes('date') || header.includes('Date') || header.includes('time')) {
          return formatDate(value);
        }
        
        // Escape quotes and commas
        const escaped = String(value).replace(/"/g, '""');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(','));
    }
    
    return csvRows.join('\n');
  };

  const downloadFile = (content: string, filename: string, type: string) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportApiaries = async () => {
    if (!user) return [];
    
    const apiaries = await apiaryService.getUserApiaries(user.uid);
    return apiaries.map(apiary => ({
      id: apiary.id,
      name: apiary.name,
      location: apiary.location,
      latitude: apiary.lat || '',
      longitude: apiary.lng || '',
      registration_date: apiary.created_at,
      user_id: apiary.user_id
    }));
  };

  const exportHives = async () => {
    if (!user) return [];
    
    const [apiaries, hives] = await Promise.all([
      apiaryService.getUserApiaries(user.uid),
      hiveService.getUserHives(user.uid)
    ]);
    
    return hives.map(hive => {
      const apiary = apiaries.find(a => a.id === hive.apiary_id);
      return {
        id: hive.id,
        name: hive.name,
        apiary_name: apiary?.name || 'Unknown',
        apiary_id: hive.apiary_id,
        queen_origin: hive.queen_origin || '',
        queen_marked: hive.queen_marked || false,
        queen_marking_color: hive.queen_marking_color || '',
        status: hive.status || '',
        established_date: hive.established,
        last_inspection: hive.last_inspection || ''
      };
    });
  };

  const exportInspections = async () => {
    if (!user) return [];
    
    const [apiaries, hives] = await Promise.all([
      apiaryService.getUserApiaries(user.uid),
      hiveService.getUserHives(user.uid)
    ]);

    // Get all inspections (not just recent ones)
    const allInspections = await inspectionService.getUserRecentInspections(user.uid, 1000); // Get up to 1000 inspections
    
    return allInspections.map(inspection => {
      const hive = hives.find(h => h.id === inspection.hive_id);
      const apiary = hive ? apiaries.find(a => a.id === hive.apiary_id) : null;
      
      return {
        id: inspection.id,
        inspection_date: inspection.date,
        hive_name: hive?.name || 'Unknown',
        hive_id: inspection.hive_id,
        apiary_name: apiary?.name || 'Unknown',
        apiary_id: hive?.apiary_id || '',
        weather: inspection.weather || '',
        queen_sighted: inspection.queen_sighted || false,
        temperament: inspection.temperament || '',
        varroa_check: inspection.varroa_check || false,
        health_rating: inspection.health_rating || '',
        notes: inspection.notes || '',
        created_at: inspection.created_at
      };
    });
  };

  const handleExport = async () => {
    if (!user) {
      setMessage('❌ Please log in to export data');
      return;
    }

    setExporting(true);
    setMessage('');

    try {
      let data: any[] | { [key: string]: any } = [];
      let filename = '';
      let headers: string[] = [];

      switch (exportScope) {
        case 'apiaries':
          data = await exportApiaries();
          filename = `beebase-pro-apiaries-${new Date().toISOString().split('T')[0]}`;
          headers = ['id', 'name', 'location', 'latitude', 'longitude', 'registration_date', 'user_id'];
          break;

        case 'hives':
          data = await exportHives();
          filename = `beebase-pro-hives-${new Date().toISOString().split('T')[0]}`;
          headers = ['id', 'name', 'apiary_name', 'apiary_id', 'queen_origin', 'queen_marked', 'queen_marking_color', 'status', 'established_date', 'last_inspection'];
          break;

        case 'inspections':
          data = await exportInspections();
          filename = `beebase-pro-inspections-${new Date().toISOString().split('T')[0]}`;
          headers = ['id', 'inspection_date', 'hive_name', 'hive_id', 'apiary_name', 'apiary_id', 'weather', 'queen_sighted', 'temperament', 'varroa_check', 'health_rating', 'notes', 'created_at'];
          break;

        case 'all':
        default:
          const [apiariesData, hivesData, inspectionsData] = await Promise.all([
            exportApiaries(),
            exportHives(),
            exportInspections()
          ]);
          
          if (exportType === 'json') {
            const allData = {
              export_date: new Date().toISOString(),
              user_id: user.uid,
              apiaries: apiariesData,
              hives: hivesData,
              inspections: inspectionsData
            };
            data = allData;
            filename = `beebase-pro-complete-export-${new Date().toISOString().split('T')[0]}`;
          } else {
            // For CSV, we'll export inspections as the primary data with related info
            data = inspectionsData;
            filename = `beebase-pro-complete-export-${new Date().toISOString().split('T')[0]}`;
            headers = ['id', 'inspection_date', 'hive_name', 'hive_id', 'apiary_name', 'apiary_id', 'weather', 'queen_sighted', 'temperament', 'varroa_check', 'health_rating', 'notes', 'created_at'];
          }
          break;
      }

      if (data.length === 0 && exportScope !== 'all') {
        setMessage(`❌ No ${exportScope} found to export`);
        return;
      }

      let content: string;
      let mimeType: string;
      
      if (exportType === 'json') {
        const jsonData = Array.isArray(data) ? { data, export_date: new Date().toISOString() } : data;
        content = JSON.stringify(jsonData, null, 2);
        mimeType = 'application/json';
        filename += '.json';
      } else {
        // For CSV, ensure we have array data
        const csvData = Array.isArray(data) ? data : 
                       (typeof data === 'object' && data.inspections) ? data.inspections : [];
        content = convertToCSV(csvData, headers);
        mimeType = 'text/csv';
        filename += '.csv';
      }

      downloadFile(content, filename, mimeType);
      
      // Track the export
      trackDataExport(`${exportScope}_${exportType}`);
      
      const recordCount = Array.isArray(data) ? data.length : 
                         (typeof data === 'object' && data.inspections) ? data.inspections.length : 0;
      
      setMessage(`✅ Successfully exported ${recordCount} records to ${filename}`);

    } catch (error) {
      console.error('Export error:', error);
      setMessage(`❌ Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ color: '#2c3e50', marginBottom: '10px' }}>📊 Export Data</h2>
        <p style={{ color: '#666', fontSize: '16px' }}>
          Export your beekeeping data for BeeBase compliance, backup, or analysis in external tools.
        </p>
      </div>

      <div style={{ 
        backgroundColor: '#f8f9fa', 
        padding: '20px', 
        borderRadius: '8px', 
        marginBottom: '20px',
        border: '1px solid #e9ecef'
      }}>
        <h3 style={{ margin: '0 0 15px 0', color: '#2c3e50' }}>Export Options</h3>
        
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#2c3e50' }}>
            What to export:
          </label>
          <select 
            value={exportScope} 
            onChange={(e) => setExportScope(e.target.value as any)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              width: '200px'
            }}
          >
            <option value="all">Complete Export (All Data)</option>
            <option value="inspections">Inspections Only</option>
            <option value="apiaries">Apiaries Only</option>
            <option value="hives">Hives Only</option>
          </select>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#2c3e50' }}>
            Export format:
          </label>
          <select 
            value={exportType} 
            onChange={(e) => setExportType(e.target.value as any)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              width: '200px'
            }}
          >
            <option value="csv">CSV (Spreadsheet)</option>
            <option value="json">JSON (Data)</option>
          </select>
        </div>
      </div>

      <button 
        onClick={handleExport}
        disabled={exporting}
        style={{
          backgroundColor: exporting ? '#6c757d' : '#E6A825',
          color: 'white',
          border: 'none',
          padding: '12px 24px',
          borderRadius: '6px',
          fontSize: '16px',
          fontWeight: 'bold',
          cursor: exporting ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        {exporting ? '🔄 Exporting...' : '📥 Export Data'}
      </button>

      {message && (
        <div style={{
          marginTop: '20px',
          padding: '15px',
          borderRadius: '6px',
          backgroundColor: message.includes('❌') ? '#f8d7da' : '#d4edda',
          color: message.includes('❌') ? '#721c24' : '#155724',
          border: message.includes('❌') ? '1px solid #f5c6cb' : '1px solid #c3e6cb'
        }}>
          {message}
        </div>
      )}

      <div style={{ 
        marginTop: '30px', 
        padding: '20px', 
        backgroundColor: '#fff3cd', 
        border: '1px solid #ffeaa7',
        borderRadius: '6px'
      }}>
        <h4 style={{ margin: '0 0 10px 0', color: '#856404' }}>💡 Export Information</h4>
        <ul style={{ color: '#856404', margin: 0, paddingLeft: '20px' }}>
          <li><strong>CSV format</strong> - Perfect for Excel, Google Sheets, or database imports</li>
          <li><strong>JSON format</strong> - Ideal for developers or advanced data analysis</li>
          <li><strong>Complete Export</strong> - Includes all your apiaries, hives, and inspections with relationships</li>
          <li><strong>BeeBase Compliance</strong> - All exports include required fields for government reporting</li>
        </ul>
      </div>
    </div>
  );
};

export default ExportPage;
